import { useEffect, useMemo, useState } from "react";
import omit from "lodash/fp/omit";
import classNames from "classnames";
import TableViewToggles, { type TableViewTogglesViewType } from "@rio-cloud/rio-uikit/TableViewToggles";
import { SCREEN_SM } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import type { SortDirectionType } from "@rio-cloud/rio-uikit/SortDirection";
import useTableSelection from "@rio-cloud/rio-uikit/useTableSelection";
import LoadMoreButton from "@rio-cloud/rio-uikit/LoadMoreButton";
import Toolbar, { ColumnDetailsMap, ColumnLabel } from "./Toolbar";
import Table, { Vehicle } from "./Table";
import { useT } from "@hooks";
import { useStoredTableSettings } from "@hooks/useStoredTableSettings";
import SelectionBar, { type SelectionBarProps } from "../SelectionBar";
import type { SharedTableSelection } from "../SharedTable";
import type { ToolbarOriginFiltersProps } from "../ToolbarOriginFilters";

const defaultColumnOrder = [
  "date",
  "amount",
  "branch",
  "segment",
  "customer",
  "vehicle",
  "driver",
  "category",
  "subcategory",
  "status",
  "trip",
  "paymentMethod",
];
const defaultHiddenColumns = ["trip", "paymentMethod"];
const disabledColumns = [""];

const demoColumnsDetails: ColumnDetailsMap = {};

const sortKeyMap: Record<string, keyof Vehicle | [keyof Vehicle, keyof Vehicle]> = {
  date: "sortableDate",
  amount: "sortableAmount",
};

export type TableExpensesProps = {
  viewType: TableViewTogglesViewType;
  data: Vehicle[];
  startDate: string;
  endDate: string;
  totalData: number;
  handleStartDate: (date: string) => void;
  handleEndDate: (date: string) => void;
  onLoadMore: () => void;
  handleRefresh: () => void;
  handleDownload: () => void;
  openModal?: (open: boolean) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onDeleteFile?: (id: string) => void;
  onGetFile?: (id: string, category: string) => void;
  onObservation?: (id: string) => void;
  onAudit?: (id: string) => void;
  isLoadingMore?: boolean;
  isLoadingTable?: boolean;
  searchValue: string;
  onSearchValueChange: (newSearchValue: string) => void;
  selection?: SharedTableSelection<Vehicle>;
  selectionBar?: SelectionBarProps;
  originFilters?: ToolbarOriginFiltersProps;
};

const TableExpenses = (props: TableExpensesProps) => {
  const t = useT();

  const columnLabels: ColumnLabel = {
    date: t("table.column.date"),
    amount: t("table.column.amount"),
    branch: t("table.column.branch"),
    segment: t("table.column.segment"),
    customer: t("table.column.supplier"),
    vehicle: t("table.column.vehicle"),
    driver: t("table.column.driver"),
    trip: t("table.column.trip"),
    category: t("table.column.category"),
    subcategory: t("table.column.subcategory"),
    taxes: t("table.column.taxes"),
    status: t("table.column.status"),
    paymentMethod: t("table.column.paymentMethod"),
  };

  const {
    viewType: externalViewType,
    data,
    startDate,
    endDate,
    handleEndDate,
    handleStartDate,
    totalData,
    onLoadMore,
    handleDownload,
    handleRefresh,
    onEdit,
    onObservation,
    onAudit,
    onDeleteFile,
    onGetFile,
    isLoadingMore = false,
    isLoadingTable = false,
    searchValue,
    onSearchValueChange,
    selection,
    selectionBar,
    originFilters,
  } = props;

  const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);

  const {
    columnOrder,
    hiddenColumns,
    columnsDetails,
    viewType,
    handleColumnChange,
    handleColumnDetailsChange,
    handleViewTypeChange,
  } = useStoredTableSettings<ColumnDetailsMap>({
    storageKey: "expenses",
    defaultColumnOrder,
    defaultHiddenColumns,
    defaultColumnsDetails: demoColumnsDetails,
    defaultViewType: externalViewType || TableViewToggles.VIEW_TYPE_TABLE,
    forcedViewType: window.innerWidth < SCREEN_SM ? TableViewToggles.VIEW_TYPE_SINGLE_CARD : undefined,
  });

  const transformDate = (dateString: string) => {
    const [day, month, year] = dateString.split("/");
    return `${year}-${month}-${day}`;
  };

  const transformAmount = (amountString: string) => {
    // Remove "R$ ", pontos de milhar e troca vírgula por ponto
    const cleaned = amountString
      .replace(/R\$\s?/g, "")
      .replaceAll(".", "")
      .replace(",", ".");
    return Number.parseFloat(cleaned) || 0;
  };

  const transformedData = data.map((item) => ({
    ...item,
    sortableDate: transformDate(item.date),
    sortableAmount: transformAmount(item.amount),
  }));

  const [localSortKey, setLocalSortKey] = useState<keyof Vehicle | null>("sortableDate");
  const [localSortDirection, setLocalSortDirection] = useState<SortDirectionType>("asc");

  const sortedData = useMemo(() => {
    if (!localSortKey) return transformedData;

    return [...transformedData].sort((a, b) => {
      const aValue = a[localSortKey];
      const bValue = b[localSortKey];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.localeCompare(bValue);
        return localSortDirection === "asc" ? comparison : -comparison;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return localSortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [transformedData, localSortKey, localSortDirection]);

  const { tableRef, updateRowSelection, onActiveRowChange } = useTableSelection<Vehicle>({
    tableData: data,
    idKey: "id" as keyof Vehicle,
  });

  useEffect(() => updateRowSelection(), [searchValue, updateRowSelection]);

  const handleToggleTableSettingsDialog = () => setShowTableSettingsDialog(!showTableSettingsDialog);
  const handleSearchValueChange = (newSearchValue: string) => onSearchValueChange(newSearchValue);

  const handleSortChange = (event: React.MouseEvent<HTMLTableCellElement>) => {
    const newSortBy = event.currentTarget.dataset.sortby;

    if (newSortBy) {
      const newSortKey = sortKeyMap[newSortBy] || newSortBy;

      if (newSortKey === localSortKey) {
        if (localSortDirection === "asc") {
          setLocalSortDirection("desc");
        } else if (localSortDirection === "desc") {
          setLocalSortKey(null);
        }
      } else {
        setLocalSortKey(newSortKey as keyof Vehicle);
        setLocalSortDirection("asc");
      }
    }
  };

  const handleCardSortChange = (newSortBy: string, newSortDir: SortDirectionType) => {
    setLocalSortKey(newSortBy as keyof Vehicle);
    setLocalSortDirection(newSortDir);
  };

  const columns = columnOrder.filter((name) => !hiddenColumns.includes(name));

  const withoutHidden = omit(hiddenColumns);
  const filteredRows = sortedData.map((vehicle) => ({ ...withoutHidden(vehicle) })) as Vehicle[];

  const rows = filteredRows;

  const tableClassNames = classNames(
    "table",
    "table-layout-fixed",
    "table-column-overflow-hidden",
    "table-bordered",
    "table-sticky",
    "table-head-filled",
    viewType === TableViewToggles.VIEW_TYPE_TABLE && "min-width-500",
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD && "table-cards table-single-card",
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS && "table-cards table-multi-cards"
  );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = columns.map((column) => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === localSortKey,
      disabled: false,
    };
  });

  return (
    <div className="height-100pct">
      <Toolbar
        searchValue={searchValue}
        handleSearchValueChange={handleSearchValueChange}
        viewType={viewType}
        handleViewTypeChange={handleViewTypeChange}
        handleToggleTableSettingsDialog={handleToggleTableSettingsDialog}
        showTableSettingsDialog={showTableSettingsDialog}
        handleColumnChange={handleColumnChange}
        defaultColumnOrder={defaultColumnOrder}
        defaultHiddenColumns={defaultHiddenColumns}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        columnLabels={columnLabels}
        disabledColumns={disabledColumns}
        columnsDetails={columnsDetails}
        handleColumnDetailsChange={handleColumnDetailsChange}
        startDate={startDate}
        handleStartDate={handleStartDate}
        endDate={endDate}
        handleEndDate={handleEndDate}
        handleDownload={handleDownload}
        handleRefresh={handleRefresh}
        originFilters={originFilters}
        openModal={(open) => props.openModal?.(open)}
      />
      {selectionBar && <SelectionBar {...selectionBar} />}
      {localSortKey && !isViewTypeTable && (
        <TableCardsSorting
          className="display-none display-flex-sm align-items-center"
          selectOptions={cardSortingSelectOptions}
          sortName={localSortKey as string}
          sortOrder={localSortDirection}
          onSortChange={handleCardSortChange}
        />
      )}
      <Table
        rows={rows}
        isViewTypeTable={isViewTypeTable}
        tableRef={tableRef}
        tableClassNames={tableClassNames}
        columns={columns}
        columnsDetails={columnsDetails}
        columnLabels={columnLabels}
        sortKey={localSortKey as any}
        sortDirection={localSortDirection}
        handleSortChange={handleSortChange}
        onActiveRowChange={onActiveRowChange}
        onEdit={onEdit}
        onDeleteFile={onDeleteFile}
        onGetFile={onGetFile}
        onObservation={onObservation}
        onAudit={onAudit}
        isLoading={isLoadingTable}
        selection={selection}
      />

      {rows?.length > 0 ? (
        <LoadMoreButton
          loaded={rows.length}
          total={totalData}
          onLoadMore={onLoadMore}
          loadMoreMessage={isLoadingMore ? "Carregando..." : "Carregar mais"}
          noMoreMessage={t("tolls.table.loadMore.noMore")}
          isInteractive={!isLoadingMore}
        />
      ) : null}
    </div>
  );
};

export default TableExpenses;
